import { useState } from "react";
import { WEDDING } from "../../data/content";
import { searchGuests } from "../../lib/guests";
import type { Invitation } from "../../lib/types";

interface Props {
  onFound: (invitation: Invitation) => void;
}

export function GuestSearch({ onFound }: Props) {
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<"idle" | "searching">("idle");
  const [error, setError] = useState("");
  // Populated when a surname matched more than one household.
  const [choices, setChoices] = useState<Invitation[] | null>(null);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    const trimmed = query.trim();
    if (!trimmed) {
      setError("Please add the name on your invitation.");
      return;
    }

    setStatus("searching");
    setError("");
    setChoices(null);

    try {
      const matches = await searchGuests(trimmed);
      if (matches.length === 0) {
        setError(
          "We could not find that name. Try the spelling on your envelope, or a last name on its own.",
        );
      } else if (matches.length === 1) {
        onFound(matches[0]);
      } else {
        setChoices(matches);
      }
    } catch {
      setError("Something went wrong looking that up. Please try again in a moment.");
    } finally {
      setStatus("idle");
    }
  }

  return (
    <div className="rsvp__panel">
      <p className="body rsvp__lede">
        Kindly reply by <span className="rsvp__deadline">{WEDDING.rsvpDeadlineLabel}</span>. One
        form per household — the name on your invitation works best.
      </p>
      <form onSubmit={handleSubmit} className="search__row" noValidate>
        <label className="field">
          <span className="field__label">Find your invitation</span>
          <input
            className="field__input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="As it appears on the invitation"
            autoComplete="name"
            enterKeyHint="search"
          />
        </label>
        <button type="submit" className="btn btn--primary" disabled={status === "searching"}>
          {status === "searching" ? "Searching…" : "Find my name"}
        </button>
      </form>

      <p className="fine search__hint">
        One reply per household. Search any name on the invitation and you can answer for
        everyone on it.
      </p>

      {/* Live region so the outcome reaches screen readers without a focus jump. */}
      <div aria-live="polite">
        {error && <p className="form-error">Error: {error}</p>}

        {choices && (
          <>
            <p className="fine" style={{ marginBottom: "var(--s1)" }}>
              We found more than one. Which is yours?
            </p>
            <ul className="search__results">
              {choices.map((invitation) => (
                <li key={invitation.id}>
                  <button
                    type="button"
                    className="search__result"
                    onClick={() => onFound(invitation)}
                  >
                    <span className="search__result-name">{invitation.displayName}</span>
                    <p className="search__result-members">
                      {invitation.memberNames.join(", ")}
                    </p>
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}
